import { useState, useRef, useEffect, type Dispatch, type SetStateAction } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useEarlySignalsConfig } from '../../hooks/useEarlySignalsConfig'
import {
  invokeEarlySignalsChat,
  invokeEarlySignalsIntentClassify,
} from '../../services/invokeEarlySignalsChat'
import type { ChatMessage as ApiChatMessage } from '../../services/invokeEarlySignalsChat'
import { ChatMarkdown } from './ChatMarkdown'
import {
  tryAnswerEarlySignalsFromDb,
  listSessionFromUtterance,
  resolveNarrativeSupplier,
  type EarlySignalsDbAnswer,
} from './tryAnswerEarlySignalsFromDb'
import { classifyEarlySignalsIntent } from './classifyEarlySignalsIntent'
import { answerFromLlmClassification } from './answerFromLlmClassification'
import { buildRiskAnalystFactsPack } from './riskAnalystFactsPack'
import { SupplierRiskHeatmapChatCard } from './SupplierRiskHeatmapChatCard'
import { DbTableChatCard } from './DbTableChatCard'
import {
  bareSupplierNameCandidate,
  resolvePendingQuestionsSupplier,
  type RiskRatingSupplier,
} from './supplierRiskRatingsTable'
import { resolveSupplierProfile, type ProfileMitigation } from './tryAnswerRiskRatingsFromDb'
import type { DbTableResult } from './dbTableAnswers'
import {
  isRefineLastTableAsk,
  mergeSupplierListFilters,
  recoverListSessionFromDbTableTitle,
  shouldClearListSession,
  type RiskAnalystListSession,
} from './riskAnalystSession'
import { extractSupplierListFilters } from './earlySignalsDbIntent'
import { logRiskAnalystMiss } from './riskAnalystMissLog'
import {
  bodyWithoutNextQuestion,
  inheritThreadFocus,
  isPlatformRiskQuestion,
  nextQuestionFrom,
  PLATFORM_DATA_SENTENCE,
} from './riskAnalystThread'
import { loadRiskAnalystChat, saveRiskAnalystChat } from './riskAnalystChatPersist'
import { EsPageHeader } from './ui'
import { type ProcurementTypeFilter } from './esPortfolioFilters'

type UiChatMessage = ApiChatMessage & {
  scorecard?: RiskRatingSupplier
  scorecardBrands?: string[]
  scorecardMitigations?: ProfileMitigation[]
  dbTable?: DbTableResult
  choices?: string[]
}

function RiskAnalystQuickLinks() {
  const { isTabEnabled } = useEarlySignalsConfig()
  const links: { to: string; label: string }[] = []
  if (isTabEnabled('specialReports')) {
    links.push({ to: '/early-signals/special-reports', label: 'Special reports' })
  }
  if (isTabEnabled('supplierRiskIntake')) {
    links.push({ to: '/early-signals/intake', label: 'Risk intake' })
  }
  if (links.length === 0) return null
  return (
    <p className="text-sm text-es-muted -mt-1 mb-0">
      {links.map((link, i) => (
        <span key={link.to}>
          {i > 0 && <span className="mx-2 text-es-hairline" aria-hidden>
            ·
          </span>}
          <Link
            to={link.to}
            className="font-medium text-es-signal hover:underline underline-offset-2"
          >
            {link.label}
          </Link>
        </span>
      ))}
    </p>
  )
}

const WELCOME_MESSAGE: UiChatMessage = {
  role: 'assistant',
  content:
    'I am your Risk analyst. Ask about a supplier or next best actions. Answers use platform data only — and lists are shown in tables when helpful. For Intelligence view ratings, ask e.g. “risk assessment of Gissler & Pass GmbH in a table by rating.”',
}

const QUICK_ACTIONS = [
  { label: "What's new", icon: 'fa-bolt', prompt: 'whats new today?' },
  {
    label: 'High-risk suppliers',
    icon: 'fa-triangle-exclamation',
    prompt: 'Show high-risk suppliers in a table',
  },
  {
    label: 'Active mitigations',
    icon: 'fa-shield-halved',
    prompt: 'Show active mitigations in a table',
  },
  {
    label: 'Special reports',
    icon: 'fa-file-lines',
    prompt: 'Show special reports in a table',
  },
  { label: 'This supplier', icon: 'fa-id-card', prompt: 'Tell me about [supplier]' },
] as const

/** GPT-6 Sol narrative (facts pack) can exceed 20s; keep under a minute for UX. */
const REPLY_TIMEOUT_MS = 45000

function withReplyTimeout<T>(work: Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('That reply took too long. Please ask again.'))
    }, REPLY_TIMEOUT_MS)
    work.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      }
    )
  })
}

function suggestionLines(content: string): string[] {
  const found: string[] = []
  const next = content.match(/Next question:\s*[“"]([^”"]+)[”"]/i)
  if (next?.[1]) found.push(next[1].trim())
  const re = /[“"]([^”"]{6,160})[”"]/g
  let match: RegExpExecArray | null
  while ((match = re.exec(content))) found.push(match[1].trim())
  return found
}

function isPastedSuggestion(
  trimmed: string,
  prior: Array<{ role: string; content: string }>
): boolean {
  const needle = trimmed.trim().toLowerCase()
  for (let i = prior.length - 1; i >= 0; i--) {
    if (prior[i].role !== 'assistant') continue
    return suggestionLines(prior[i].content).some((line) => line.toLowerCase() === needle)
  }
  return false
}

function withNextQuestion(content: string, question: string): string {
  if (/Next question:/i.test(content)) return content
  return `${content}\n\nNext question: “${question}”`
}

function appendDbAnswer(
  setMessages: Dispatch<SetStateAction<UiChatMessage[]>>,
  fromDb: EarlySignalsDbAnswer
) {
  if (fromDb.kind === 'supplier_profile') {
    setMessages((prev) => [
      ...prev,
      {
        role: 'assistant',
        content: withNextQuestion(
          fromDb.content,
          `What is the highest risk in ${fromDb.supplier.name || 'this supplier'}`
        ),
        scorecard: fromDb.supplier,
        scorecardBrands: fromDb.brands,
        scorecardMitigations: fromDb.mitigations,
      },
    ])
    return
  }
  if (fromDb.kind === 'table') {
    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: fromDb.content, dbTable: fromDb.table },
    ])
    return
  }
  if (fromDb.kind === 'text') {
    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: fromDb.content, choices: fromDb.choices },
    ])
  }
}

function FollowUpButton({
  content,
  disabled,
  onAsk,
}: {
  content: string
  disabled: boolean
  onAsk: (question: string) => void
}) {
  const question = nextQuestionFrom(content)
  if (!question) return null
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onAsk(question)}
      className="mt-2 px-2.5 py-1 rounded-[var(--radius-md)] border border-es-hairline bg-es-white text-left text-[11px] font-semibold text-es-signal hover:border-es-signal disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {question}
    </button>
  )
}

export function AIView() {
  const location = useLocation()
  const navigate = useNavigate()
  const { effectiveCompanyId, isAdmin } = useAuth()
  const hydrated = useRef(false)

  const [messages, setMessages] = useState<UiChatMessage[]>(() => {
    const saved = loadRiskAnalystChat()
    if (saved?.messages?.length) return saved.messages as UiChatMessage[]
    return [WELCOME_MESSAGE]
  })
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [awaitingSupplierFor, setAwaitingSupplierFor] = useState<
    'questions_to_ask' | 'next_best_actions' | null
  >(() => loadRiskAnalystChat()?.awaitingSupplierFor ?? null)
  const [listSession, setListSession] = useState<RiskAnalystListSession | null>(
    () => loadRiskAnalystChat()?.listSession ?? null
  )
  const [lastSupplier, setLastSupplier] = useState<string | null>(
    () => loadRiskAnalystChat()?.lastSupplier ?? null
  )
  const [lastCategory, setLastCategory] = useState<string | null>(
    () => loadRiskAnalystChat()?.lastCategory ?? null
  )
  const lastSupplierRef = useRef(lastSupplier)
  const lastCategoryRef = useRef(lastCategory)
  lastSupplierRef.current = lastSupplier
  lastCategoryRef.current = lastCategory
  const inputRef = useRef<HTMLInputElement>(null)
  const listSessionRef = useRef<RiskAnalystListSession | null>(listSession)
  listSessionRef.current = listSession
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  /** Portfolio filter removed from RA UI — lists default to full portfolio. */
  const portfolioProcurementRef = useRef<ProcurementTypeFilter>('all')

  useEffect(() => {
    if (!hydrated.current) {
      hydrated.current = true
      return
    }
    saveRiskAnalystChat({
      messages: messages.map(
        ({ role, content, scorecard, scorecardBrands, scorecardMitigations, dbTable, choices }) => ({
          role,
          content,
          scorecard,
          scorecardBrands,
          scorecardMitigations,
          dbTable,
          choices,
        })
      ),
      listSession,
      awaitingSupplierFor,
      lastSupplier,
      lastCategory,
    })
  }, [messages, listSession, awaitingSupplierFor, lastSupplier, lastCategory])

  const rememberListSession = (trimmed: string, fromDb: EarlySignalsDbAnswer) => {
    const session = listSessionRef.current
    if (session && isRefineLastTableAsk(trimmed) && fromDb.kind === 'table') {
      const next = {
        ...session,
        filters: mergeSupplierListFilters(
          session.filters,
          extractSupplierListFilters(trimmed)
        ),
      }
      listSessionRef.current = next
      setListSession(next)
      return
    }
    const routed = classifyEarlySignalsIntent(trimmed)
    if (fromDb.kind === 'table' && routed.kind === 'tool') {
      if (
        routed.tool === 'list_suppliers' ||
        routed.tool === 'count_filtered_suppliers' ||
        routed.tool === 'group_suppliers_by'
      ) {
        const next = listSessionFromUtterance(
          trimmed,
          routed.tool,
          portfolioProcurementRef.current
        )
        listSessionRef.current = next
        setListSession(next)
        return
      }
    }
    if (fromDb.kind === 'narrative' || fromDb.kind === 'supplier_profile') {
      listSessionRef.current = null
      setListSession(null)
      return
    }
    if (routed.kind === 'tool' && shouldClearListSession(routed.tool)) {
      listSessionRef.current = null
      setListSession(null)
    }
  }

  /** Prefer live ref; on refine with no session, rebuild from last Intelligence view table title. */
  const resolveListSessionForAsk = (trimmed: string): RiskAnalystListSession | null => {
    let session = listSessionRef.current
    if (session || !isRefineLastTableAsk(trimmed)) return session
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.role !== 'assistant' || !m.dbTable?.title) continue
      const recovered = recoverListSessionFromDbTableTitle(m.dbTable.title)
      if (recovered) {
        listSessionRef.current = recovered
        setListSession(recovered)
        return recovered
      }
      break
    }
    return null
  }

  useEffect(() => {
    const prompt = (location.state as { prompt?: string } | null)?.prompt
    if (typeof prompt === 'string' && prompt.trim()) {
      setInputValue(prompt.trim())
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.pathname, location.state, navigate])

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages])

  const runNarrative = async (
    narrative: 'next_best_actions' | 'questions_to_ask',
    supplierName: string | null,
    history: ApiChatMessage[]
  ) => {
    if (narrative === 'questions_to_ask') {
      setAwaitingSupplierFor(null)
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            'Supplier questions are paused until master-data question sets are ready. Ask about platform risk data, next best actions, or open a supplier profile instead.',
        },
      ])
      return
    }
    if (!(supplierName || '').trim()) {
      setAwaitingSupplierFor(narrative)
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            'Which supplier should I prepare next best actions for? For example: “Next best actions for CHEP” or “NBA for Gissler & Pass”.',
        },
      ])
      return
    }
    setAwaitingSupplierFor(null)
    const factsPack = await buildRiskAnalystFactsPack({
      mode: narrative,
      supplierName,
      companyId: effectiveCompanyId,
    })
    const { content } = await withReplyTimeout(
      invokeEarlySignalsChat({
        messages: history,
        useRag: false,
        factsPack,
      })
    )
    const follow = supplierName
      ? `What is the highest risk in ${supplierName}`
      : 'Which supplier should I open?'
    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: withNextQuestion(content, follow) },
    ])
  }

  const rememberResolved = (fromDb: EarlySignalsDbAnswer) => {
    if (fromDb.kind === 'supplier_profile') {
      const name = (fromDb.supplier.name || '').trim()
      if (name) setLastSupplier(name)
      return
    }
    if (fromDb.kind === 'text') {
      if (fromDb.resolvedSupplier) setLastSupplier(fromDb.resolvedSupplier)
      if (fromDb.resolvedCategory) setLastCategory(fromDb.resolvedCategory)
    }
  }

  const focusSupplierPlaceholder = (prompt: string) => {
    setInputValue(prompt)
    setError(null)
    requestAnimationFrame(() => {
      const el = inputRef.current
      if (!el) return
      const start = prompt.indexOf('[supplier]')
      el.focus()
      if (start >= 0) el.setSelectionRange(start, start + '[supplier]'.length)
    })
  }

  const sendMessage = async (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || loading) return
    if (trimmed.includes('[supplier]')) {
      focusSupplierPlaceholder(trimmed)
      return
    }

    const userMessage: UiChatMessage = { role: 'user', content: trimmed }
    setMessages((prev) => [...prev, userMessage])
    setInputValue('')
    setError(null)
    setLoading(true)

    const nextMessages: ApiChatMessage[] = [...messages, userMessage].map(({ role, content }) => ({
      role,
      content,
    }))

    try {
      await withReplyTimeout((async () => {
      // A pasted suggestion is a new question, not a reply to "which supplier?"
      let pendingAwait = awaitingSupplierFor
      if (pendingAwait && isPastedSuggestion(trimmed, messages)) {
        pendingAwait = null
        setAwaitingSupplierFor(null)
      }

      // Resume after "Which supplier should I prepare … for?"
      if (pendingAwait) {
        const classified = classifyEarlySignalsIntent(trimmed)
        const isOtherIntent =
          classified.kind === 'tool' ||
          (classified.kind === 'narrative' && classified.tool !== pendingAwait)
        if (isOtherIntent) {
          setAwaitingSupplierFor(null)
        } else {
          const pendingName = resolvePendingQuestionsSupplier(trimmed)
          if (pendingName) {
            const mode = pendingAwait
            setAwaitingSupplierFor(null)
            await runNarrative(mode, pendingName, nextMessages)
            return
          }
        }
      }

      const asked = inheritThreadFocus(trimmed, {
        lastSupplier: lastSupplierRef.current,
        lastCategory: lastCategoryRef.current,
      })
      const sessionForAsk = resolveListSessionForAsk(asked)
      const dbOptions = {
        session: sessionForAsk,
        defaultProcurementType: portfolioProcurementRef.current,
        companyId: effectiveCompanyId,
        linkSpend: isAdmin,
      }
      let fromDb = await tryAnswerEarlySignalsFromDb(asked, dbOptions)

      if (fromDb == null) {
        const bare = bareSupplierNameCandidate(asked)
        if (bare) fromDb = await resolveSupplierProfile(bare)
      }

      // Closed-catalog JSON classifier only after a keyword miss.
      if (fromDb == null) {
        try {
          const classified = await invokeEarlySignalsIntentClassify(asked)
          if (classified.intent === 'clarify') {
            listSessionRef.current = null
            setListSession(null)
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                content:
                  'Which supplier do you mean? For example: “Tell me about CHEP” or “brands associated with Gissler”.',
              },
            ])
            return
          }
          const mapped = await answerFromLlmClassification({
            ...classified,
            userText: asked,
            session: listSessionRef.current,
            defaultProcurementType: portfolioProcurementRef.current,
            companyId: effectiveCompanyId,
            linkSpend: isAdmin,
          })
          if (mapped === 'clarify' || (mapped == null && isPlatformRiskQuestion(asked))) {
            listSessionRef.current = null
            setListSession(null)
            setMessages((prev) => [
              ...prev,
              { role: 'assistant', content: PLATFORM_DATA_SENTENCE },
            ])
            return
          }
          if (mapped != null) fromDb = mapped
        } catch {
          listSessionRef.current = null
          setListSession(null)
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: PLATFORM_DATA_SENTENCE },
          ])
          return
        }
      }

      if (fromDb != null) {
        if (fromDb.kind === 'narrative') {
          listSessionRef.current = null
          setListSession(null)
          if (fromDb.supplierName) {
            const resolved = await resolveNarrativeSupplier(fromDb.supplierName)
            if (resolved.kind === 'ambiguous') {
              setMessages((prev) => [
                ...prev,
                { role: 'assistant', content: resolved.content, choices: resolved.names },
              ])
              return
            }
            if (resolved.kind === 'none') {
              setMessages((prev) => [...prev, { role: 'assistant', content: resolved.content }])
              return
            }
            setLastSupplier(resolved.name)
            await runNarrative(fromDb.narrative, resolved.name, nextMessages)
            return
          }
          await runNarrative(fromDb.narrative, null, nextMessages)
          return
        }
        setAwaitingSupplierFor(null)
        rememberResolved(fromDb)
        rememberListSession(asked, fromDb)
        appendDbAnswer(setMessages, fromDb)
        return
      }

      logRiskAnalystMiss(trimmed, 'fallthrough_rag')
      setAwaitingSupplierFor(null)
      listSessionRef.current = null
      setListSession(null)
      const { content } = await invokeEarlySignalsChat({
        messages: nextMessages,
        useRag: true,
      })
      setMessages((prev) => [...prev, { role: 'assistant', content }])
      })())
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong.'
      setError(message)
      setMessages((prev) => [...prev, { role: 'assistant', content: `Error: ${message}` }])
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    sendMessage(inputValue)
  }

  const handleQuickAction = (prompt: string) => {
    if (prompt.includes('[supplier]')) {
      const named = lastSupplierRef.current?.trim()
      if (named) {
        sendMessage(prompt.replace('[supplier]', named))
        return
      }
      focusSupplierPlaceholder(prompt)
      return
    }
    setInputValue(prompt)
    sendMessage(prompt)
  }

  return (
    <div className="p-3 md:p-4 max-w-5xl mx-auto flex flex-col gap-3">
      <EsPageHeader
        className="!mb-2"
        title="Risk analyst"
        beta
        description="Answers come from the suppliers, alerts, and mitigations in your database."
        eyebrow="AI"
      />
      <RiskAnalystQuickLinks />

      <div className="flex-1 bg-es-white rounded-[var(--radius-lg)] border border-es-soft shadow-[var(--shadow-card)] overflow-hidden flex flex-col">
        <div
          ref={scrollContainerRef}
          className="flex-1 px-4 py-3 overflow-y-auto space-y-3 min-h-[200px] max-h-[56vh]"
        >
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex gap-2.5 items-start ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 bg-es-signal rounded-[var(--radius-md)] flex items-center justify-center text-white shrink-0">
                  <i className="fa-solid fa-wand-magic-sparkles text-sm" aria-hidden />
                </div>
              )}
              <div
                className={`p-3 rounded-2xl border ${
                  msg.scorecard || msg.dbTable ? 'max-w-[min(100%,42rem)]' : 'max-w-[90%]'
                } ${
                  msg.role === 'user'
                    ? 'bg-es-signal text-white border-es-signal rounded-tr-none'
                    : 'bg-es-haze border-es-soft rounded-tl-none text-es-body'
                }`}
              >
                {msg.role === 'assistant' ? (
                  <>
                    {msg.scorecard ? (
                      <SupplierRiskHeatmapChatCard
                        supplier={msg.scorecard}
                        brands={msg.scorecardBrands}
                        mitigations={msg.scorecardMitigations}
                      />
                    ) : msg.dbTable ? (
                      <DbTableChatCard
                        table={msg.dbTable}
                        onAsk={(question) => sendMessage(question)}
                        askDisabled={loading}
                      />
                    ) : (
                      <ChatMarkdown content={bodyWithoutNextQuestion(msg.content)} />
                    )}
                    <FollowUpButton
                      content={msg.content}
                      disabled={loading}
                      onAsk={(question) => sendMessage(question)}
                    />
                    {msg.choices && msg.choices.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {msg.choices.map((name) => (
                          <button
                            key={name}
                            type="button"
                            disabled={loading}
                            onClick={() => sendMessage(`Tell me about ${name}`)}
                            className="px-2 py-1 rounded-[var(--radius-md)] border border-es-hairline bg-es-white text-[11px] font-semibold text-es-signal hover:border-es-signal disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {name}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                )}
              </div>
              {msg.role === 'user' && (
                <div className="w-8 h-8 bg-es-soft rounded-[var(--radius-md)] flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-user text-es-deep text-sm" aria-hidden />
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-2.5 items-start">
              <div className="w-8 h-8 bg-es-signal rounded-[var(--radius-md)] flex items-center justify-center text-white shrink-0">
                <i className="fa-solid fa-wand-magic-sparkles text-sm" aria-hidden />
              </div>
              <div className="bg-es-haze p-3 rounded-2xl rounded-tl-none border border-es-soft">
                <span className="text-sm text-es-muted flex items-center gap-2">
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden />
                  Searching platform data…
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="px-3 py-3 border-t border-es-soft bg-es-haze/50">
          {error && (
            <p className="text-sm text-red-600 mb-2" role="alert">
              {error}
            </p>
          )}
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask about a supplier or next best actions…"
                className="w-full pl-4 pr-12 py-2.5 bg-es-white border border-es-hairline rounded-[var(--radius-lg)] shadow-[var(--shadow-card)] focus:outline-none focus:ring-2 focus:ring-es-signal/20 focus:border-es-signal font-medium text-sm text-es-body disabled:opacity-70 disabled:cursor-not-allowed"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value)
                  setError(null)
                }}
                disabled={loading}
                aria-label="Ask Risk analyst"
              />
              <button
                type="submit"
                disabled={loading || !inputValue.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-es-signal text-white rounded-[var(--radius-md)] flex items-center justify-center hover:bg-es-deep transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Send"
              >
                <i className="fa-solid fa-paper-plane text-sm" aria-hidden />
              </button>
            </div>
          </form>
          <div className="flex flex-wrap justify-center gap-2 mt-3">
            {QUICK_ACTIONS.map(({ label, icon, prompt }) => (
              <button
                key={label}
                type="button"
                onClick={() => handleQuickAction(prompt)}
                disabled={loading}
                className="px-3 py-1.5 bg-es-white border border-es-hairline rounded-[var(--radius-md)] text-[11px] font-semibold uppercase tracking-wider text-es-muted hover:border-es-signal hover:text-es-signal transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className={`fa-solid ${icon}`} aria-hidden />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
